import { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Factory, ExternalLink, ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

const PHOTOS = [
  { url: "/img/stabilimento/4onlc0o7_IMG_4809.jpeg", alt: "Linea di produzione delle celle fotovoltaiche" },
  { url: "/img/stabilimento/hbez4x4u_IMG_4807.jpeg", alt: "Deposizione automatizzata sui moduli" },
  { url: "/img/stabilimento/3k54v6jw_IMG_4806.jpeg", alt: "Robot di manipolazione dei pannelli" },
  { url: "/img/stabilimento/pcshphh5_IMG_4796.jpeg", alt: "Impianto fotovoltaico installato su capannone industriale" },
];
export const COMPANY_PHOTOS = PHOTOS;

// Foto azienda condivise (landing + outlook), gestibili da Parametri → Immagini azienda.
// Fallback alle foto di default se l'endpoint pubblico non risponde o è vuoto.
export function useCompanyPhotos() {
  const [photos, setPhotos] = useState(PHOTOS);
  useEffect(() => {
    let alive = true;
    api.get("/public/company-photos")
      .then(({ data }) => {
        if (alive && data && Array.isArray(data.photos) && data.photos.length) {
          setPhotos(data.photos.map((p) => ({ url: p.url, alt: p.alt || "Immagine azienda", descrizione: p.descrizione || "" })));
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return photos;
}

const ARIAL = { fontFamily: "Arial, Helvetica, sans-serif" };

export default function CompanyPositioning({ compact = false }) {
  const photos = useCompanyPhotos();
  const [idx, setIdx] = useState(0);
  const go = (n) => setIdx((n + photos.length) % photos.length);

  useEffect(() => { setIdx(0); }, [photos.length]);

  useEffect(() => {
    if (photos.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % photos.length), 4000);
    return () => clearInterval(t);
  }, [photos.length]);

  return (
    <Card className="rounded-xl border-border overflow-hidden pdf-slot" data-testid="outlook-azienda" style={ARIAL}>
      <div className="grid sm:grid-cols-2">
        <div className="p-4 flex flex-col">
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold mb-2 flex items-center gap-1.5" style={ARIAL}>
            <Factory className="h-3.5 w-3.5 text-primary" strokeWidth={1.5} /> La nostra azienda
          </div>
          <p className="text-sm font-semibold leading-snug" style={ARIAL}>
            Dal 2007, la prima fabbrica di pannelli fotovoltaici Made in Italy con la garanzia del chiavi in mano diretto al cliente finale.
          </p>
          <p className="text-xs text-muted-foreground leading-snug mt-2" style={ARIAL}>
            I nostri pannelli fotovoltaici uniscono tecnica costruttiva e design industriale per diventare i pannelli fotovoltaici più robusti e sicuri del mercato.
          </p>
          <a
            href="https://www.torri.solar"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="outlook-azienda-link"
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline w-fit"
            style={ARIAL}
          >
            Scopri le nostre tecnologie e la nostra storia su www.torri.solar
            <ExternalLink className="h-3 w-3" />
          </a>

          {!compact && (
          <div className="mt-3 pt-3 border-t border-border/60" data-testid="outlook-prisma">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold mb-1.5 flex items-baseline gap-1.5 flex-wrap" style={ARIAL}>
              <ShieldCheck className="h-3.5 w-3.5 text-primary self-center" strokeWidth={1.5} /> La tecnologia Prisma 4.0
              <span className="normal-case tracking-normal font-medium text-foreground/70">· un modello costruttivo unico</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug" style={ARIAL}>
              Il Vetro con spessore maggiorato ed un pattern prismatico, i copolimeri avanzati come il POE ad alta densità ed una ricetta di laminazione unica, sono solo alcune tra le scelte industriali e le innovazioni che caratterizzano il nostro modello costruttivo unico.
            </p>
            <p className="text-xs text-muted-foreground leading-snug mt-2" style={ARIAL}>
              La tecnologia Prisma 4.0 è sviluppata per proteggere il circuito in silicio al proprio interno. I risultati ottenuti sono straordinari ed unici.
            </p>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="rounded-lg border border-border bg-secondary/30 p-2" data-testid="prisma-grandine">
                <div className="text-sm font-bold text-primary" style={ARIAL}>8 VOLTE</div>
                <div className="text-xs text-muted-foreground leading-snug mt-0.5" style={ARIAL}>più resistente a grandine da 45mm sparata a 110 km/h, nello stesso punto di collisione all'infinito.</div>
              </div>
              <div className="rounded-lg border border-border bg-secondary/30 p-2" data-testid="prisma-neve">
                <div className="text-sm font-bold text-primary" style={ARIAL}>3 VOLTE</div>
                <div className="text-xs text-muted-foreground leading-snug mt-0.5" style={ARIAL}>più robusta fino a 750 kg/mq di resistenza a carico neve: robustezza e durabilità senza eguali.</div>
              </div>
            </div>
          </div>
          )}
        </div>

        <div className={`relative aspect-[16/10] sm:aspect-auto ${compact ? "sm:min-h-[120px]" : "sm:min-h-[300px]"} bg-secondary`} data-testid="outlook-azienda-slider">
          {photos.map((p, i) => (
            <img
              key={i}
              src={p.url}
              alt={p.alt}
              loading="lazy"
              data-testid={`outlook-azienda-photo-${i}`}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${i === idx ? "opacity-100" : "opacity-0"}`}
            />
          ))}
          {(photos[idx]?.alt || photos[idx]?.descrizione) && (
            <div className="absolute top-0 left-0 right-0 z-[1] px-3 pt-2.5 pb-8 pointer-events-none" data-testid="outlook-azienda-caption" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.88), rgba(0,0,0,0.5) 65%, rgba(0,0,0,0))" }}>
              {photos[idx]?.alt && <span className="block text-white text-xs sm:text-sm font-semibold leading-snug" style={{ ...ARIAL, color: "#ffffff", textShadow: "0 1px 3px rgba(0,0,0,0.95)" }}>{photos[idx].alt}</span>}
              {photos[idx]?.descrizione && <span className="block text-white/90 text-[11px] sm:text-xs font-normal leading-snug mt-0.5" style={{ ...ARIAL, color: "rgba(255,255,255,0.92)", textShadow: "0 1px 3px rgba(0,0,0,0.95)" }}>{photos[idx].descrizione}</span>}
            </div>
          )}
          {photos.length > 1 && (
            <>
              <button type="button" onClick={() => go(idx - 1)} aria-label="Foto precedente" data-testid="azienda-prev"
                className="absolute left-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-black/45 text-white flex items-center justify-center hover:bg-black/65 transition-colors">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => go(idx + 1)} aria-label="Foto successiva" data-testid="azienda-next"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-black/45 text-white flex items-center justify-center hover:bg-black/65 transition-colors">
                <ChevronRight className="h-4 w-4" />
              </button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                {photos.map((_, i) => (
                  <button key={i} type="button" onClick={() => go(i)} aria-label={`Vai alla foto ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-white" : "w-1.5 bg-white/50"}`} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
